(() => {
  const ROOT_SELECTOR = '[data-top-interview-slider]';
  const DRAG_THRESHOLD_PX = 10;

  function getScrollStep(track) {
    const slide = track.querySelector('[data-slide]');
    if (!slide) return 0;

    const slideRect = slide.getBoundingClientRect();
    const trackStyle = window.getComputedStyle(track);
    const gap = parseFloat(trackStyle.columnGap || trackStyle.gap || '0') || 0;
    return Math.round(slideRect.width + gap);
  }

  function initRoot(root) {
    if (!root || root.dataset.initialized === 'true') return;

    const viewport = root.querySelector('[data-viewport]');
    const track = root.querySelector('[data-track]');
    const prevBtn = root.querySelector('[data-prev]');
    const nextBtn = root.querySelector('[data-next]');

    const modal = root.querySelector('[data-modal]');
    const modalBackdrop = root.querySelector('[data-modal-backdrop]');
    const modalClose = root.querySelector('[data-modal-close]');
    const modalTitle = root.querySelector('[data-modal-title]');
    const modalPlayer = root.querySelector('[data-modal-player]');

    if (!viewport || !track || !prevBtn || !nextBtn || !modal || !modalBackdrop || !modalClose || !modalPlayer) {
      return;
    }

    root.dataset.initialized = 'true';

    let lastActiveElement = null;
    let handleTrapFocus = null;
    let isPointerDown = false;
    let isDragging = false;
    let suppressNextClick = false;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let pointerId = null;

    function getFocusableElements() {
      const focusables = modal.querySelectorAll(
        'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"]),video'
      );
      return Array.from(focusables).filter((el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));
    }

    function updateNavDisabled() {
      const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth;
      const sl = Math.round(viewport.scrollLeft);
      prevBtn.disabled = sl <= 0;
      nextBtn.disabled = sl >= Math.round(maxScrollLeft);
    }

    function openModal({ templateId, title, kind, externalType, externalId }) {

      lastActiveElement = document.activeElement;

      if (modalTitle) modalTitle.textContent = title || 'INTERVIEW';

      modalPlayer.innerHTML = '';
      if (kind === 'external' && externalType && externalId) {
        if (externalType === 'youtube') {
          const iframe = document.createElement('iframe');
          iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(externalId)}?autoplay=1&rel=0`;
          iframe.allow =
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
          iframe.allowFullscreen = true;
          iframe.title = title || 'YouTube video player';
          modalPlayer.appendChild(iframe);
        } else if (externalType === 'vimeo') {
          const iframe = document.createElement('iframe');
          iframe.src = `https://player.vimeo.com/video/${encodeURIComponent(externalId)}?autoplay=1&title=0&byline=0&portrait=0`;
          iframe.allow = 'autoplay; fullscreen; picture-in-picture';
          iframe.allowFullscreen = true;
          iframe.title = title || 'Vimeo video player';
          modalPlayer.appendChild(iframe);
        }
      } else {
        const template = templateId ? root.querySelector(`#${CSS.escape(templateId)}`) : null;
        if (!template || !(template instanceof HTMLTemplateElement)) {
          // eslint-disable-next-line no-console
          console.warn('[top-interview-slider] Video template not found:', templateId);
          return;
        }
        modalPlayer.appendChild(template.content.cloneNode(true));
      }

      modal.hidden = false;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('top-interview-slider--locked');

      // Focus trap
      const focusables = getFocusableElements();
      const first = focusables[0] || modalClose;
      const last = focusables[focusables.length - 1] || modalClose;
      handleTrapFocus = (e) => {
        if (e.key !== 'Tab') return;
        const current = document.activeElement;
        if (e.shiftKey) {
          if (current === first || current === modal) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (current === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      modal.addEventListener('keydown', handleTrapFocus);

      modalClose.focus();

      const video = modalPlayer.querySelector('video');
      if (video && typeof video.play === 'function') {
        // Reset and play on user gesture
        try {
          video.currentTime = 0;
        } catch {
          // ignore
        }
        video.play().catch(() => {
          // Autoplay can be blocked; controls are shown so user can start playback.
        });
      }
    }

    function closeModal() {
      if (modal.hidden) return;

      const video = modalPlayer.querySelector('video');
      if (video && typeof video.pause === 'function') {
        try {
          video.pause();
        } catch {
          // ignore
        }
      }

      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      modal.hidden = true;

      // Stop playback (destroy video)
      modalPlayer.innerHTML = '';
      document.body.classList.remove('top-interview-slider--locked');

      if (handleTrapFocus) {
        modal.removeEventListener('keydown', handleTrapFocus);
        handleTrapFocus = null;
      }

      if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
        lastActiveElement.focus();
      }
      lastActiveElement = null;
    }

    prevBtn.addEventListener('click', () => {
      const step = getScrollStep(track);
      viewport.scrollBy({ left: -step, behavior: 'smooth' });
    });

    nextBtn.addEventListener('click', () => {
      const step = getScrollStep(track);
      viewport.scrollBy({ left: step, behavior: 'smooth' });
    });

    viewport.addEventListener('scroll', () => {
      window.requestAnimationFrame(updateNavDisabled);
    });

    updateNavDisabled();

    // Drag-to-scroll (mouse/touch/pen via Pointer Events)
    // - Allows click & drag on the viewport to scroll horizontally (mouse only)
    // - Suppresses the *next* click after a drag to avoid accidental modal open
    viewport.addEventListener('pointerdown', (e) => {
      // Mouse only（タッチはネイティブスクロールに任せる）
      if (e.pointerType !== 'mouse') return;
      if (e.button !== 0) return;

      isPointerDown = true;
      isDragging = false;
      suppressNextClick = false;
      startX = e.clientX;
      startY = e.clientY;
      startScrollLeft = viewport.scrollLeft;
    });

    viewport.addEventListener('pointermove', (e) => {
      if (!isPointerDown) return;

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (!isDragging) {
        // 横方向のドラッグが一定以上かつ縦移動より優勢な場合のみドラッグ開始
        if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
        if (Math.abs(dx) < Math.abs(dy)) return;

        isDragging = true;
        pointerId = e.pointerId;
        try {
          viewport.setPointerCapture(pointerId);
        } catch {
          // ignore
        }

        // Prevent text selection while dragging
        viewport.style.cursor = 'grabbing';
        viewport.style.userSelect = 'none';
      }

      // Keep in sync with scroll buttons state
      viewport.scrollLeft = startScrollLeft - dx;
      window.requestAnimationFrame(updateNavDisabled);

      // Stop the browser from treating it as a selection/drag
      e.preventDefault();
    });

    function endPointerDrag() {
      if (!isPointerDown) return;

      if (isDragging) {
        const moved = Math.abs(viewport.scrollLeft - startScrollLeft);
        if (moved >= DRAG_THRESHOLD_PX) suppressNextClick = true;
      }

      isPointerDown = false;
      isDragging = false;
      pointerId = null;
      viewport.style.cursor = '';
      viewport.style.userSelect = '';
    }

    viewport.addEventListener('pointerup', () => endPointerDrag());
    viewport.addEventListener('pointercancel', () => endPointerDrag());
    viewport.addEventListener('lostpointercapture', () => endPointerDrag());

    root.addEventListener('click', (e) => {
      // 直前にドラッグでスクロールした場合、次のクリックだけ抑止
      if (suppressNextClick) {
        e.preventDefault();
        e.stopPropagation();
        suppressNextClick = false;
        return;
      }

      const trigger = e.target.closest('[data-card-trigger]');
      if (!trigger || !root.contains(trigger)) return;

      const templateId = trigger.getAttribute('data-video-template-id') || '';
      const title = trigger.getAttribute('data-title') || '';
      const kind = trigger.getAttribute('data-video-kind') || 'shopify';
      const externalType = trigger.getAttribute('data-external-video-type') || '';
      const externalId = trigger.getAttribute('data-external-video-id') || '';
      openModal({ templateId, title, kind, externalType, externalId });
    });

    modalClose.addEventListener('click', closeModal);
    modalBackdrop.addEventListener('click', closeModal);

    modal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeModal();
    });
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => initRoot(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const sectionRoot = event.target;
    if (!sectionRoot) return;
    sectionRoot.querySelectorAll(ROOT_SELECTOR).forEach((root) => initRoot(root));
  });
})();

